<script setup>
import { computed } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';
import {
  FIELD_INPUT_CLASS,
  FIELD_SELECT_CLASS,
  FIELD_TEXTAREA_CLASS,
  operationalFlowForKind,
} from '../constants';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Identity block of the builder: what the survey is called, which action type
 * triggers it and whether it is the default questionnaire for that type.
 */
const props = defineProps({
  form: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);

const { t, kindOptions, kindLabel, validationMessage } = useSurveyLabels();

const name = computed({
  get: () => props.form.name,
  set: value => emit('update', { name: value }),
});

const description = computed({
  get: () => props.form.description,
  set: value => emit('update', { description: value }),
});

const kind = computed({
  get: () => props.form.kind,
  set: value => {
    const currentFlow = props.form.settings?.operational_flow;
    const followsCurrentDefault =
      !currentFlow || currentFlow === operationalFlowForKind(props.form.kind);

    emit('update', {
      kind: value,
      settings: {
        ...props.form.settings,
        operational_flow: followsCurrentDefault
          ? operationalFlowForKind(value)
          : currentFlow,
      },
    });
  },
});

const active = computed({
  get: () => props.form.active,
  set: value => emit('update', { active: value }),
});

const defaultForKind = computed({
  get: () => props.form.default_for_kind,
  set: value => emit('update', { default_for_kind: value }),
});

const defaultHelper = computed(() =>
  t('SURVEYS.EDITOR.META.DEFAULT_HELPER', { kind: kindLabel(props.form.kind) })
);
</script>

<template>
  <section class="grid gap-4">
    <div class="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <LaisField
        :label="t('SURVEYS.EDITOR.META.NAME')"
        :error="validationMessage(errors.name)"
        required
      >
        <input
          v-model="name"
          type="text"
          :class="FIELD_INPUT_CLASS"
          :placeholder="t('SURVEYS.EDITOR.META.NAME_PLACEHOLDER')"
        />
      </LaisField>

      <LaisField
        :label="t('SURVEYS.EDITOR.META.KIND')"
        :helper="t('SURVEYS.EDITOR.META.KIND_HELPER')"
        :error="validationMessage(errors.kind)"
      >
        <select v-model="kind" :class="FIELD_SELECT_CLASS">
          <option
            v-for="option in kindOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </LaisField>
    </div>

    <LaisField
      :label="t('SURVEYS.EDITOR.META.DESCRIPTION')"
      :helper="t('SURVEYS.EDITOR.META.DESCRIPTION_HELPER')"
    >
      <textarea
        v-model="description"
        rows="2"
        :class="FIELD_TEXTAREA_CLASS"
        :placeholder="t('SURVEYS.EDITOR.META.DESCRIPTION_PLACEHOLDER')"
      />
    </LaisField>

    <div class="grid gap-3 sm:grid-cols-2">
      <LaisToggle
        v-model="active"
        :label="t('SURVEYS.EDITOR.META.ACTIVE')"
        :helper="t('SURVEYS.EDITOR.META.ACTIVE_HELPER')"
      />
      <LaisToggle
        v-model="defaultForKind"
        :label="t('SURVEYS.EDITOR.META.DEFAULT')"
        :helper="defaultHelper"
      />
    </div>
  </section>
</template>
